import { isAxiosError } from 'axios';
import { useToast } from 'primevue/usetoast';

interface ToastInput {
  title: string;
  message: string;
}

type TranslationKey = 'answers' | 'auditLogs' | 'channels' | 'roles' | 'skills' | 'targets' | 'tariffs' | 'teams' | 'users' | 'rules' | 'ios';

function isNotFoundResponse(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

function isBadRequestResponse(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 400;
}

export function useErrorHandling() {
  const { t } = useI18n();
  const toast = useToast();

  function showToast(input: ToastInput) {
    toast.add({
      severity: 'error',
      summary: input.title,
      detail: input.message,
      life: 10_000,
    });
  }

  /**
   * Handles generic errors where the cause is unknown or no specific handler fits.
   * Use this if none of the other specific handlers are appropriate.
   * @param translateUnique - Optional: a ToastInput or a function returning a ToastInput using the translation function.
   */
  function handleGenericError(translateUnique?: ToastInput | ((tFn: typeof t) => ToastInput)) {
    const input = typeof translateUnique === 'function'
      ? translateUnique(t)
      : translateUnique;

    showToast(input ?? {
      title: 'Fehler',
      message: 'Es ist ein unerwarteter Fehler aufgetreten',
    });
  }

  /**
   * Handles errors that occur while fetching options.
   * Use this when an error happens during retrieval of selectable options.
   */
  function handleGetOptionsError() {
    showToast({
      title: t('errors.toast.common.get'),
      message: t('errors.toast.common.options'),
    });
  }

  /**
   * Handles errors that occur while fetching a list of entities.
   * Use this when an error happens during retrieval of a collection (e.g., users, roles).
   * @param tkey - The entity type (e.g., 'channels', 'roles').
   */
  function handleGetAllError(tkey: TranslationKey) {
    showToast({
      title: t('errors.toast.common.get'),
      message: t(`errors.toast.${tkey}.getAll`),
    });
  }

  /**
   * Handles errors that occur while fetching a single entity.
   * Use this when an error happens during retrieval of a specific item.
   * @param error - The error object.
   * @param tkey - The entity type.
   */
  function handleGetError(error: unknown, tkey: TranslationKey) {
    // ToDo: do we want a special message for NotFound? generic load error seems to be fine
    if (isNotFoundResponse(error)) {
      showToast({
        title: t('errors.toast.common.get'),
        message: t(`errors.toast.${tkey}.get`),
      });
    }

    showToast({
      title: t('errors.toast.common.get'),
      message: t(`errors.toast.${tkey}.get`),
    });
  }

  /**
   * Handles errors that occur while creating a new entity.
   * Use this when an error happens during creation (e.g., new user, channel).
   * @param error - The error object.
   * @param tkey - The entity type.
   */
  function handleCreateError(error: unknown, tkey: TranslationKey) {
    if (isBadRequestResponse(error)) {
      showToast({
        title: t('errors.toast.validation.title'),
        message: t('errors.toast.validation.message'),
      });
      return;
    }

    showToast({
      title: t('errors.toast.common.create'),
      message: t(`errors.toast.${tkey}.create`),
    });
  }

  /**
   * Handles errors that occur while updating an existing entity.
   * Use this when an error happens during update (e.g., updating a user or target).
   * @param error - The error object.
   * @param tkey - The entity type.
   */
  function handleUpdateError(error: unknown, tkey: TranslationKey) {
    // ToDo: do we want a special message for NotFound? generic load error seems to be fine
    if (isNotFoundResponse(error)) {
      showToast({
        title: t('errors.toast.common.update'),
        message: t(`errors.toast.${tkey}.update`),
      });
    }

    if (isBadRequestResponse(error)) {
      showToast({
        title: t('errors.toast.validation.title'),
        message: t('errors.toast.validation.message'),
      });
      return;
    }

    showToast({
      title: t('errors.toast.common.update'),
      message: t(`errors.toast.${tkey}.update`),
    });
  }

  /**
   * Handles errors that occur while deleting an entity.
   * Use this when an error happens during deletion (e.g., deleting a team or channel).
   * @param error - The error object.
   * @param tkey - The entity type.
   */
  function handleDeleteError(error: unknown, tkey: TranslationKey) {
    // ToDo: do we want a special message for NotFound? generic load error seems to be fine
    if (isNotFoundResponse(error)) {
      showToast({
        title: t('errors.toast.common.delete'),
        message: t(`errors.toast.${tkey}.delete`),
      });
    }

    showToast({
      title: t('errors.toast.common.delete'),
      message: t(`errors.toast.${tkey}.delete`),
    });
  }

  return {
    handleGenericError,
    handleGetOptionsError,
    handleGetError,
    handleGetAllError,
    handleCreateError,
    handleUpdateError,
    handleDeleteError,
  };
}
